import { zodResolver } from '@hookform/resolvers/zod';
import { DimensionUnit, FreightService, QuoteStatus, WeightUnit } from '@pelicargo/types';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { EmailTemplateName } from '../../../../api/src/lib/codegen/email';
import { useAuth } from '../../context/AuthProvider';
import {
  AirlineFormValue,
  airlineValidationSchema,
  ensureFreightServiceIs,
  getDefaultAirlinesFormFromQuote,
  getDefaultTruckingsFormFromQuote,
  getDefaultWarehousesFormFromQuote,
  getOpenerByEmailTemplate,
  getSharedAirlineDefaultsFromRequest,
  getSharedInlandDefaultsFromRequest,
  SharedEmailOverrides,
  truckingValidationSchema,
  warehouseValidationSchema,
} from '../../utils/email';
import { Quote, Request } from '../../utils/types';

export type ProviderQuotingValues = SharedEmailOverrides & {
  isLongEmail: boolean;
  providers: AirlineFormValue[];
};

const validationSchema = z.object({
  senderContactId: z.coerce.number().min(1, 'You must specify a sender contact'),
  isLongEmail: z.boolean().default(false),
  clipboard: z.boolean().optional().default(false),
  contentOnly: z.boolean().optional().default(false),

  origin: z.string(),
  destination: z.string(),
  weightUnit: z.nativeEnum(WeightUnit),
  dimensionUnit: z.nativeEnum(DimensionUnit),

  customMessage: z.string().optional(),
});

const sectionSchema = z
  .object({
    airlines: validationSchema.extend({ providers: z.array(airlineValidationSchema).optional() }),
    trucks: validationSchema.extend({ providers: z.array(truckingValidationSchema).optional() }),
    warehouses: validationSchema.extend({ providers: z.array(warehouseValidationSchema).optional() }),
  })
  .superRefine((data, ctx) => {
    const providerTypes = ['airlines', 'trucks', 'warehouses'] as const;
    const hasAtLeastOneProvider = providerTypes.some((type) => data[type].providers.length > 0);
    if (!hasAtLeastOneProvider) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'You must add at least one provider',
      });
    }

    const hasAtLeastOneRecipient = providerTypes.some((type) =>
      data[type].providers?.some((provider) => provider.recipients?.length > 0),
    );
    if (!hasAtLeastOneRecipient) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'You must add at least one recipient',
      });
    }
  });

export type ProviderQuotingSection = z.infer<typeof sectionSchema>;

export const useProviderQuotingForm = (request?: Request, quotes?: Quote[]) => {
  const airlineCustomMessage = useMemo(() => getOpenerByEmailTemplate(EmailTemplateName.AIRLINE_QUOTING), []);
  const truckCustomMessage = useMemo(() => getOpenerByEmailTemplate(EmailTemplateName.TRUCKING_QUOTING), []);
  const warehouseCustomMessage = useMemo(() => getOpenerByEmailTemplate(EmailTemplateName.WAREHOUSE_QUOTING), []);

  const { user } = useAuth();

  const defaultAirlines = useMemo(() => {
    return quotes
      ?.filter((quote) => quote.status !== QuoteStatus.InProgress)
      ?.filter(ensureFreightServiceIs([FreightService.AFR]))
      ?.map(getDefaultAirlinesFormFromQuote);
  }, [quotes]);

  const defaultTrucks = useMemo(() => {
    return quotes
      ?.filter((quote) => quote.status !== QuoteStatus.InProgress)
      ?.filter(ensureFreightServiceIs([FreightService.EXW]))
      ?.map(getDefaultTruckingsFormFromQuote);
  }, [quotes]);

  const defaultWarehouses = useMemo(() => {
    return quotes
      ?.filter((quote) => quote.status !== QuoteStatus.InProgress)
      ?.filter(ensureFreightServiceIs([FreightService.FCA]))
      ?.map(getDefaultWarehousesFormFromQuote);
  }, [quotes]);

  const defaultValues = useMemo(() => {
    const sharedAirlineDefaults = getSharedAirlineDefaultsFromRequest(request);
    const sharedInlandDefaults = getSharedInlandDefaultsFromRequest(request);
    const defaultSenderContactId = user?.contact_id && user?.contact_id > 0 ? user.contact_id : undefined;

    return {
      airlines: {
        ...sharedAirlineDefaults,
        customMessage: airlineCustomMessage,
        senderContactId: defaultSenderContactId,
        providers: defaultAirlines,
      },
      trucks: {
        ...sharedInlandDefaults,
        origin: request?.cargo_origin?.zip_code || '',
        destination: sharedInlandDefaults?.origin || '',
        customMessage: truckCustomMessage,
        senderContactId: defaultSenderContactId,
        providers: defaultTrucks,
      },
      warehouses: {
        ...sharedInlandDefaults,
        destination: sharedInlandDefaults?.origin || '',
        customMessage: warehouseCustomMessage,
        senderContactId: defaultSenderContactId,
        providers: defaultWarehouses,
      },
    };
  }, [
    request,
    airlineCustomMessage,
    truckCustomMessage,
    warehouseCustomMessage,
    defaultAirlines,
    defaultTrucks,
    defaultWarehouses,
    user?.contact_id,
  ]);

  const methods = useForm<Partial<ProviderQuotingSection>>({
    mode: 'onSubmit',
    resolver: zodResolver(sectionSchema),
    defaultValues,
  });

  return { methods };
};
